import { useCheckAttachment } from '@my/check-api/hooks';
import { ImageOff, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * A defect photo on a check: a thumbnail that opens full size in place.
 *
 * Full size opens in an overlay on this page, never a new tab — the image is an
 * object URL, and navigating to one is exactly what must not happen for the
 * signature (an SVG run as a page executes its scripts). Photos follow the same
 * rule so there is one way to view evidence, not two.
 */
export function AnswerPhoto({ checkId, answerId }: { checkId: string; answerId: string }) {
  const { t } = useTranslation('checks');
  const { url, isLoading, isError } = useCheckAttachment(checkId, answerId);
  const [open, setOpen] = useState(false);

  if (isError) {
    return (
      <span className="mt-1 inline-flex items-center gap-1 text-[10px] text-slate-400">
        <ImageOff className="h-3 w-3" /> {t('detail.photoUnavailable')}
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => url && setOpen(true)}
        disabled={!url}
        className="mt-1.5 block h-20 w-20 overflow-hidden rounded-sm border border-slate-300 bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label={t('detail.openPhoto')}
      >
        {isLoading || !url ? (
          <span className="block h-full w-full animate-pulse bg-slate-200" />
        ) : (
          <img src={url} alt={t('detail.defectPhoto')} className="h-full w-full object-cover" />
        )}
      </button>
      {open && url && <Lightbox url={url} onClose={() => setOpen(false)} alt={t('detail.defectPhoto')} />}
    </>
  );
}

/** The driver's signature, drawn on the phone and stored as an SVG. */
export function CheckSignature({ checkId }: { checkId: string }) {
  const { t } = useTranslation('checks');
  const { url, isLoading, isError } = useCheckAttachment(checkId, null);

  if (isError) return <p className="mt-2 text-[10px] text-slate-400">{t('detail.signatureUnavailable')}</p>;
  if (isLoading || !url) return <div className="mt-2 h-16 w-60 animate-pulse rounded-sm bg-slate-100" />;

  return (
    <div className="mt-2 inline-block rounded-sm border border-slate-200 bg-white p-2">
      {/* In an <img>, an SVG cannot run script — the reason it is never opened any other way. */}
      <img src={url} alt={t('detail.driverSignature')} className="h-16 max-w-[15rem] object-contain" />
    </div>
  );
}

function Lightbox({ url, alt, onClose }: { url: string; alt: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
        aria-label="Close"
      >
        <X className="h-5 w-5" />
      </button>
      <img
        src={url}
        alt={alt}
        className="max-h-full max-w-full rounded-sm object-contain"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}
